import { useEffect, useState } from "preact/hooks";
import { api, type AiCatalogResponse, type AiProviderCatalog as ApiAiProviderCatalog } from "./api";
import type { AiProvider } from "./uiPrefs";

export interface AiModelCatalogItem {
  id: string;
  value: string;
  label: string;
  description: string;
  kind: "general" | "reasoning" | "coding";
  recommended?: boolean;
  freeFriendly?: boolean;
}

export interface AiFreeTierInfo {
  /** O provedor oferece uso gratuito real (sem precisar cartão)? */
  available: boolean;
  /** Texto curto pra badge/header (ex: "Grátis no Google AI Studio"). */
  label: string;
  /** Texto explicativo mais longo (1-2 linhas). */
  note?: string;
}

export interface AiProviderCatalog {
  label: string;
  keyUrl: string;
  keyLabel: string;
  /** Quando informado, é exibido no onboarding como badge "GRÁTIS" + nota. */
  freeTier?: AiFreeTierInfo;
  connectionModes?: {
    platform: boolean;
    byok: boolean;
    connectedAccount: boolean;
  };
  connectionNote?: string;
  models: AiModelCatalogItem[];
}

export const AI_PROVIDER_CATALOG: Record<AiProvider, AiProviderCatalog> = {
  gemini: {
    label: "Gemini",
    keyUrl: "https://aistudio.google.com/apikey",
    keyLabel: "Google AI Studio",
    freeTier: {
      available: true,
      label: "Grátis no Google AI Studio",
      note: "Você ganha cota gratuita ao gerar a chave — não precisa cartão. Inclui Gemini 2.5 Flash com limite generoso pra uso pessoal."
    },
    models: [
      {
        id: "gemini-2.5-flash",
        value: "gemini-2.5-flash",
        label: "Gemini 2.5 Flash",
        description: "Rápido, econômico e ótimo para uso no plano grátis.",
        kind: "general",
        recommended: true,
        freeFriendly: true
      },
      {
        id: "gemini-2.5-pro",
        value: "gemini-2.5-pro",
        label: "Gemini 2.5 Pro",
        description: "Mais forte para explicações longas e raciocínio.",
        kind: "reasoning"
      },
      {
        id: "gemini-code-assist",
        value: "gemini-2.5-pro",
        label: "Gemini Técnico",
        description:
          "Perfil técnico inspirado no Gemini Code Assist, usando Gemini para lógica, programação e respostas mais analíticas.",
        kind: "coding"
      }
    ]
  },
  openai: {
    label: "OpenAI",
    keyUrl: "https://platform.openai.com/api-keys",
    keyLabel: "OpenAI Platform",
    models: [
      {
        id: "gpt-5-mini",
        value: "gpt-5-mini",
        label: "GPT-5 mini",
        description: "Rápido para perguntas do dia a dia.",
        kind: "general",
        recommended: true
      },
      {
        id: "gpt-5.2-codex",
        value: "gpt-5.2-codex",
        label: "Codex (OpenAI)",
        description: "Mais técnico para lógica, programação e análise estruturada.",
        kind: "coding"
      }
    ]
  },
  anthropic: {
    label: "Claude",
    keyUrl: "https://console.anthropic.com/settings/keys",
    keyLabel: "Anthropic Console",
    models: [
      {
        id: "claude-sonnet-4-20250514",
        value: "claude-sonnet-4-20250514",
        label: "Claude Sonnet",
        description: "Muito bom para explicações cuidadosas e texto longo.",
        kind: "general",
        recommended: true
      },
      {
        id: "claude-agent",
        value: "claude-sonnet-4-20250514",
        label: "Claude Agent",
        description:
          "Perfil técnico para respostas mais agentic e estruturadas, inspirado no ecossistema Claude Code/Agent SDK.",
        kind: "coding"
      }
    ]
  },
  xai: {
    label: "Grok",
    keyUrl: "https://console.x.ai/",
    keyLabel: "xAI Console",
    models: [
      {
        id: "grok-4.20-reasoning",
        value: "grok-4.20-reasoning",
        label: "Grok Reasoning",
        description: "Focado em raciocínio mais profundo e análise.",
        kind: "reasoning",
        recommended: true
      }
    ]
  },
  deepseek: {
    label: "DeepSeek",
    keyUrl: "https://platform.deepseek.com/api_keys",
    keyLabel: "DeepSeek Platform",
    freeTier: {
      available: false,
      label: "Pago, mas econômico",
      note: "DeepSeek cobra por token, mas é uma das opções com melhor custo-benefício do mercado — V3 e R1 a frações do preço dos concorrentes."
    },
    models: [
      {
        id: "deepseek-chat",
        value: "deepseek-chat",
        label: "DeepSeek Chat",
        description:
          "Modelo geral (V3). Rápido, econômico, ótimo para o dia a dia.",
        kind: "general",
        recommended: true,
        freeFriendly: true
      },
      {
        id: "deepseek-reasoner",
        value: "deepseek-reasoner",
        label: "DeepSeek Reasoner",
        description:
          "Modelo de raciocínio (R1). Mostra o passo a passo do pensamento — bom para matemática, lógica e programação.",
        kind: "reasoning"
      }
    ]
  }
};

export function getProviderCatalog(provider: AiProvider): AiProviderCatalog {
  return AI_PROVIDER_CATALOG[provider];
}

let remoteCatalogCache: AiCatalogResponse | null = null;

export function useProviderCatalog(provider: AiProvider) {
  const [catalog, setCatalog] = useState<AiCatalogResponse | null>(remoteCatalogCache);

  useEffect(() => {
    if (remoteCatalogCache) return;
    let cancelled = false;
    void api
      .aiCatalog()
      .then((resp) => {
        remoteCatalogCache = resp;
        if (!cancelled) setCatalog(resp);
      })
      .catch(() => {
        if (!cancelled) setCatalog(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const providerCatalog = (catalog?.providers?.[provider] ??
    AI_PROVIDER_CATALOG[provider]) as AiProviderCatalog & ApiAiProviderCatalog;

  return {
    providerCatalog,
    strategy: catalog?.strategy ?? null,
    catalogSource: catalog ? "api" : "local"
  };
}
